/**
 * Per-module vendor agreements — shapes mirrored from backend `agreement` module.
 *
 * Every upload is a new, immutable version. Publishing notifies only the vendors
 * offering that module; they accept in-app or at onboarding.
 */
import type { VendorModuleType } from '@/features/vendors/types';

export type AgreementVersion = {
  id: string;
  module_type: VendorModuleType;
  version: number;
  original_filename: string;
  size_bytes: number;
  change_summary: string | null;
  effective_from: string;
  /** False while `effective_from` is still in the future. */
  in_effect: boolean;
  published_at: string;
  /** Relative to the API base, e.g. `/admin/agreements/files/:id`. */
  file_path: string;
};

export type AgreementModuleOverview = {
  module_type: VendorModuleType;
  latest: AgreementVersion | null;
  vendor_count: number;
  accepted_count: number;
};

export type PublishAgreementInput = {
  module_type: VendorModuleType;
  file: File;
  /** ISO date-time with offset. Omitted → effective immediately. */
  effective_from?: string;
  change_summary?: string;
};

export type PublishAgreementResult = AgreementVersion & { notified_vendors: number };

export type AgreementVersionWithCount = AgreementVersion & { accepted_count: number };

export type AgreementAcceptanceSource = 'ONBOARDING' | 'IN_APP';

export type AgreementVendorStatus = {
  vendor_profile_id: string;
  business_name: string;
  phone: string;
  accepted_at: string | null;
  source: AgreementAcceptanceSource | null;
};

export const ACCEPTANCE_SOURCE_LABEL: Record<AgreementAcceptanceSource, string> = {
  ONBOARDING: 'Onboarding',
  IN_APP: 'In-app',
};

/** Backend limit on the uploaded PDF. */
export const MAX_AGREEMENT_BYTES = 10 * 1024 * 1024;
export const MAX_CHANGE_SUMMARY = 2000;

export function agreement_file_path(id: string): string {
  return `/admin/agreements/files/${id}`;
}

/** `482 KB` / `1.4 MB`. */
export function file_size(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
